import { Filter, Quote } from 'lucide-react';
import { CardDescription, CardHeader, CardTitle } from '../ui/card';
import InfoHint from '../InfoHint';

/**
 * Шапка карточки графика по узлу — одна на все типы графиков.
 *
 * Раньше шапку каждый компонент верстал сам, и высоты не совпадали: у карточки с длинным
 * названием заголовок наезжал на подпись (CardTitle идёт с `leading-none`), а кнопки
 * ужимались, потому что текстовый блок не был `min-w-0`. В двухколоночной сетке это давало
 * разъезжающиеся ряды.
 *
 * Здесь заголовок ограничен двумя строками, пояснение узла — одной (полный текст в
 * тултипе), а блок кнопок помечен `shrink-0`. Высота шапки перестаёт зависеть от длины
 * названия, поэтому карточки любого пакета встают ровно.
 *
 * Каждая из трёх строк держит минимальную высоту, даже когда пуста. Ограничения сверху
 * (`line-clamp`, `truncate`) выравнивают только длинный текст, а короткий или отсутствующий
 * по-прежнему давал бы шапки разной высоты: у карточки без описания график начинался бы
 * выше, чем у соседа по ряду.
 */
const NodeChartCardHeader = ({
  title,
  description,
  meta,
  isLoading,
  onReload,
  onShowExamples
}) => (
  <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
    <div className="min-w-0 space-y-1">
      {/* Высота под две строки резервируется всегда: у соседней карточки в ряду заголовок
          может быть длиннее, и без резерва области графиков начинались бы на разной
          высоте — при просмотре ряда текст «прыгает». */}
      <div className="flex min-h-[2.75rem] items-start gap-1">
        <CardTitle className="line-clamp-2 text-base leading-snug" title={title}>
          {title}
        </CardTitle>
        {/* description из output_description.json — поясняет, что именно считает узел. */}
        <InfoHint text={description} label={`О графике «${title}»`} trackId="nodes.card.hint" />
      </div>
      <CardDescription className="min-h-4 text-xs leading-snug">{meta}</CardDescription>
    </div>

    <div className="flex shrink-0 items-center gap-2">
      {onShowExamples && (
        <button
          type="button"
          data-ev="nodes.card.show_examples"
          onClick={onShowExamples}
          title="Примеры звонков с цитатами"
          aria-label="Примеры"
          className="inline-flex h-8 items-center rounded-md border px-2.5 text-xs text-muted-foreground hover:bg-muted max-sm:w-8 max-sm:justify-center max-sm:px-0"
        >
          <Quote className="h-3.5 w-3.5 sm:mr-1.5" />
          <span className="max-sm:hidden">Примеры</span>
        </button>
      )}
      <button
        type="button"
        data-ev="nodes.card.reload"
        onClick={() => onReload()}
        disabled={isLoading}
        title="Обновить данные графика"
        aria-label="Обновить"
        className="inline-flex h-8 items-center rounded-md border px-2.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50 max-sm:w-8 max-sm:justify-center max-sm:px-0"
      >
        <Filter className="h-3.5 w-3.5 sm:mr-1.5" />
        <span className="max-sm:hidden">{isLoading ? 'Обновление…' : 'Обновить'}</span>
      </button>
    </div>
  </CardHeader>
);

export default NodeChartCardHeader;
